import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  Tag,
  Typography,
  Space,
  Alert,
  Skeleton,
  message,
} from 'antd';
import { ReloadOutlined, SaveOutlined, KeyOutlined } from '@ant-design/icons';
import {
  getIntegrationConfig,
  putIntegrationConfig,
  type IntegrationProvider,
  type IntegrationConfig,
} from '@/api/integrations';

const { Title, Paragraph, Text } = Typography;

// 一个 provider 的元数据 (未来加即梦/Flux 直接往 PROVIDERS 加一项).
interface ProviderMeta {
  provider: IntegrationProvider;
  name: string;
  desc: string;
  keyPlaceholder: string;
  docHint: string;
}

const PROVIDERS: ProviderMeta[] = [
  {
    provider: 'dashscope',
    name: 'DashScope / 通义千问图像编辑',
    desc: '阿里百炼 qwen-image-edit — 创意工具「图片编辑 / 精修」的主引擎 (保 ID 指令编辑)。未配置时编辑/精修自动回退自建 Kontext 兜底。',
    keyPlaceholder: 'sk-... (百炼 API Key, 北京区)',
    docHint: '阿里云百炼控制台 → API-KEY 管理获取; 模型默认 qwen-image-edit-max。',
  },
];

function sourceTag(source: IntegrationConfig['source'], hasKey: boolean) {
  if (!hasKey) return <Tag color="default">未配置</Tag>;
  if (source === 'db') return <Tag color="green">已配置 (后台)</Tag>;
  if (source === 'env') return <Tag color="blue">已配置 (环境变量)</Tag>;
  return <Tag color="default">未配置</Tag>;
}

interface FormValues {
  api_key?: string;
  base_url: string;
  model: string;
  enabled: boolean;
}

function ProviderCard({ meta }: { meta: ProviderMeta }) {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState<IntegrationConfig | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getIntegrationConfig(meta.provider);
      setCfg(data);
      form.setFieldsValue({
        api_key: '', // 永不回显明文; 留空 = 保留原 key
        base_url: data.base_url,
        model: data.model,
        enabled: data.enabled,
      });
    } catch (e) {
      message.error(`读取配置失败: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [meta.provider, form]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const updated = await putIntegrationConfig(meta.provider, {
        // 空字符串不传 → 后端保留原 key.
        api_key: values.api_key ? values.api_key : undefined,
        base_url: values.base_url,
        model: values.model,
        enabled: values.enabled,
      });
      setCfg(updated);
      form.setFieldsValue({ api_key: '' });
      message.success('已保存，worker 将在 30 秒内热更生效');
    } catch (e) {
      message.error(`保存失败: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={
        <Space>
          <KeyOutlined />
          {meta.name}
          {cfg && sourceTag(cfg.source, cfg.has_key)}
        </Space>
      }
      extra={
        <Button icon={<ReloadOutlined />} size="small" onClick={load} disabled={loading}>
          刷新
        </Button>
      }
      style={{ maxWidth: 720 }}
    >
      <Paragraph type="secondary">{meta.desc}</Paragraph>
      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            label="API Key"
            name="api_key"
            extra={
              <Text type="secondary">
                {cfg?.has_key ? '已配置。留空表示保留现有 key，仅在此输入可覆盖。' : '尚未配置，请填写。'}
                {' '}{meta.docHint}
              </Text>
            }
          >
            <Input.Password placeholder={meta.keyPlaceholder} autoComplete="off" allowClear />
          </Form.Item>
          <Form.Item label="Base URL" name="base_url">
            <Input placeholder="https://dashscope.aliyuncs.com/api/v1" />
          </Form.Item>
          <Form.Item label="模型" name="model">
            <Input placeholder="qwen-image-edit-max" />
          </Form.Item>
          <Form.Item label="启用" name="enabled" valuePropName="checked" extra="关闭后该引擎停用，编辑/精修回退自建兜底链。">
            <Switch />
          </Form.Item>
          {cfg?.updated_at && (
            <Paragraph type="secondary" style={{ marginTop: -8 }}>
              最后更新: {cfg.updated_at}
            </Paragraph>
          )}
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>
            保存
          </Button>
        </Form>
      )}
    </Card>
  );
}

export function IntegrationsPage() {
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          平台设置 · 集成密钥
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          平台级第三方服务的 API 密钥与参数。保存后 worker 自动热更，无需重新部署。密钥加密存储，页面不回显明文。
        </Paragraph>
      </div>
      <Alert
        type="info"
        showIcon
        message="密钥安全"
        description="API Key 以 AES 加密存于数据库，仅显示「已配置 / 未配置」状态，不会回传明文。"
      />
      {PROVIDERS.map((m) => (
        <ProviderCard key={m.provider} meta={m} />
      ))}
    </Space>
  );
}
